import { createContext, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase, timeoutSignal, isStaffPosPath } from '../lib/supabase';
import { useAuth } from './AuthContext';
import type { OrgSettings } from '../lib/pos-types';
import { mergeOrgSettings } from '../lib/org-settings';
import { setErrorLoggerContext } from '../lib/error-logger';

interface Organization {
  id: string;
  name: string;
  slug?: string;
  subscription_status: string;
  settings: OrgSettings | null;
  business_day_reset_hour?: number;
}

interface OrganizationContextType {
  currentOrganization: Organization | null;
  organizations: Organization[];
  loading: boolean;
  createOrganization: (name: string) => Promise<void>;
  updateOrganization: (updates: Partial<Organization>) => Promise<void>;
  updateOrgSettings: (patch: Record<string, unknown>, opts?: { remove?: string[] }) => Promise<void>;
  refetch: () => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const { user, isSuperAdmin } = useAuth();
  const { pathname } = useLocation();
  // Staff POS is code-based and consumes no owner context — never fetch org data
  // there, so a stalled owner-session request can't freeze the pilot terminal.
  const onStaffRoute = isStaffPosPath(pathname);

  // Fetch user's organizations
  const { data: organizations = [], isLoading, refetch } = useQuery({
    queryKey: ['organizations', user?.id, isSuperAdmin],
    queryFn: async () => {
      if (!user) return [];

      // 1. Get explicit memberships — abort after 6s so a stalled 4G socket
      //    retries on a fresh connection instead of hanging the app ~13s.
      const t = timeoutSignal(6000);
      const { data: memberData, error: memberError } = await supabase
        .from('organization_members')
        .select(`
          organization_id,
          organizations (
            id,
            name,
            slug,
            subscription_status,
            settings,
            business_day_reset_hour
          )
        `)
        .eq('user_id', user.id)
        .eq('active', true)
        .abortSignal(t.signal);
      t.clear();

      if (memberError) throw memberError;

      const memberOrgs = (memberData || [])
        .map((item: any) => item.organizations as Organization)
        .filter(Boolean);

      // 2. If Super Admin, fetch ALL organizations to ensure total visibility
      if (isSuperAdmin) {
        const { data: allOrgs, error: allError } = await supabase
          .from('organizations')
          .select('id, name, slug, subscription_status, settings, business_day_reset_hour')
          .order('name');
        
        if (allError) throw allError;
        
        // Merge - prioritizing membership data if any, but allOrgs is the source of truth for Admin
        return allOrgs as Organization[];
      }

      return memberOrgs;
    },
    enabled: !!user && !onStaffRoute,
    retry: 2,
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 3000),
  });

  // The active org derives from the URL slug — the same source every /:slug/… page,
  // the route guard (ClientProtectedRoute), and the slug-scoped data queries already
  // trust. We match the first path segment against the user's org slugs; a non-slug
  // surface (/, /admin, /login, transient /dashboard before RootRedirect) falls back
  // to the first membership.
  //
  // There is deliberately NO localStorage here. The visible slug is the single source
  // of truth, so switching establishment (navigating to another /:slug) instantly
  // re-scopes every consumer of currentOrganization — TVA settings, thermal printer,
  // business-day hour, customer stats — with no stale-org drift.
  const activeSlug = pathname.split('/')[1] || '';
  const currentOrganization =
    organizations.find((org) => org.slug === activeSlug) ?? organizations[0] ?? null;

  // Keep error logger aware of current user + org
  useEffect(() => {
    setErrorLoggerContext(user?.id ?? null, currentOrganization?.id ?? null);
  }, [user?.id, currentOrganization?.id]);

  const createOrganization = async (name: string) => {
    if (!user) throw new Error('Not authenticated');
    const { data: org, error: orgError } = await supabase
      .from('organizations')
      .insert({ name, owner_id: user.id })
      .select()
      .single();
    if (orgError) throw orgError;
    const { error: memberError } = await supabase
      .from('organization_members')
      .insert({ organization_id: org.id, user_id: user.id, role: 'org_owner' });
    if (memberError) throw memberError;
    await refetch();
  };

  const updateOrganization = async (updates: Partial<Organization>) => {
    if (!currentOrganization) throw new Error('No organization selected');
    const { error } = await supabase.from('organizations').update(updates).eq('id', currentOrganization.id);
    if (error) throw error;
    await refetch();
  };

  // Write-safe settings update: server-side live-merge (never a whole-column write).
  const updateOrgSettings = async (patch: Record<string, unknown>, opts?: { remove?: string[] }) => {
    if (!currentOrganization) throw new Error('No organization selected');
    await mergeOrgSettings(currentOrganization.id, patch, { remove: opts?.remove });
    await refetch();
  };

  return (
    <OrganizationContext.Provider value={{ currentOrganization, organizations, loading: isLoading, createOrganization, updateOrganization, updateOrgSettings, refetch }}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (!context) throw new Error('useOrganization must be used within OrganizationProvider');
  return context;
}